import React from "react";
import {
  Loader2,
  CheckCircle2,
  AlertTriangle,
  FileText,
  X,
} from "lucide-react";
import { UploadItem } from "../../contexts/UploadContext";
import { useCyclingWord } from "./useCyclingWord";
import { UPLOADING_WORDS, PROCESSING_WORDS } from "./cyclingWords";

interface UploadWidgetItemProps {
  upload: UploadItem;
  onNavigate: () => void;
  onDismiss: () => void;
}

const UploadWidgetItem: React.FC<UploadWidgetItemProps> = ({
  upload,
  onNavigate,
  onDismiss,
}) => {
  const getStatusIcon = () => {
    switch (upload.status) {
      case "uploading":
      case "processing":
      case "pending":
        return <Loader2 className="animate-spin text-[#1d365d]" size={20} />;
      case "complete":
        return <CheckCircle2 className="text-green-500" size={20} />;
      case "error":
        return <AlertTriangle className="text-red-500" size={20} />;
      default:
        return <FileText className="text-gray-400" size={20} />;
    }
  };

  const isFinished = upload.status === "complete" || upload.status === "error";
  const isUploading = upload.status === "uploading" || upload.status === "pending";
  const isProcessing = upload.status === "processing";

  // Estimate upload duration from total file size assuming ~500 KB/s
  const totalBytes = upload.files.reduce((sum, f) => sum + f.size, 0);
  const uploadEstimateMs = (totalBytes / 500_000) * 1000;

  const uploadingWord = useCyclingWord(UPLOADING_WORDS, isUploading, {
    estimatedDurationMs: uploadEstimateMs,
  });
  const processingWord = useCyclingWord(PROCESSING_WORDS, isProcessing, {
    progress: upload.progress,
  });

  const displayStep = (() => {
    if (isUploading) return uploadingWord;
    if (isProcessing) {
      // Once the first poll has reported the page total, show the live
      // "X of Y pages processed" count (same as the bulk upload). Until then
      // fall back to the cycling word so we don't flash "0 of 0".
      if (upload.totalPages > 0) {
        return `${upload.completedPages} of ${upload.totalPages} pages processed`;
      }
      return processingWord;
    }
    return upload.currentStep;
  })();

  return (
    <div className="px-4 py-3 border-b border-gray-100 last:border-b-0 hover:bg-gray-50">
      <div className="flex items-center gap-3">
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-gray-900 truncate">
            {upload.documentName}
          </p>
          <p
            className={`text-xs ${
              upload.status === "error" ? "text-red-500" : "text-gray-500"
            }`}
          >
            {upload.error || displayStep}
          </p>
        </div>

        <div className="flex items-center gap-2">
          {getStatusIcon()}

          {upload.status === "complete" && upload.documentId && (
            <button
              onClick={onNavigate}
              className="text-[#1d365d] hover:text-[#2d4a75] text-sm font-medium"
            >
              View
            </button>
          )}

          {isFinished && (
            <button
              onClick={onDismiss}
              className="text-gray-400 hover:text-gray-600 p-1 rounded-lg hover:bg-gray-100"
              title="Dismiss"
            >
              <X size={16} />
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default UploadWidgetItem;
